import {
  Box,
  Chip,
  type ChipProps,
  Divider,
  Paper,
  Stack,
  Typography,
  darken,
  lighten,
  styled,
  useTheme,
} from "@mui/material"
import type { PropsWithChildren, ReactNode } from "react"
import { pathOr, stringToPath } from "remeda"

const DEFAULT_HEIGHT = "100px"

export const SummaryBanner = ({
  height = DEFAULT_HEIGHT,
  rightItems,
  children,
}: PropsWithChildren<{ height?: string; rightItems?: ReactNode }>) => (
  <Paper component={Box} px={2.5} py={1.5} height={height}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      height="100%"
    >
      <Stack
        direction="row"
        gap={2.5}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {children}
      </Stack>
      {rightItems}
    </Stack>
  </Paper>
)

export const SummarySection = ({
  title,
  children,
}: PropsWithChildren<{ title?: string }>) => (
  <Stack gap={1}>
    {title && <SectionTitle>{title}</SectionTitle>}
    <Stack direction="row" gap={1} width="fit-content">
      {children}
    </Stack>
  </Stack>
)

export const SummaryChipDivider = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{ height: "30px", alignSelf: "flex-end" }}
  />
)

export type SummaryInfoDisplayProps = {
  label: string
  count: number
  labelColor?: string
  baseColor: string
  variant: ChipProps["variant"]
} & Partial<Pick<ChipProps, "onClick" | "sx">>

export const SummaryInfoDisplay = ({
  label,
  count,
  labelColor,
  baseColor,
  variant = "outlined",
  onClick,
  sx,
}: SummaryInfoDisplayProps) => {
  const theme = useTheme()

  const finalHoverBackgroundColor = pathOr(
    theme.palette,
    stringToPath(baseColor),
    baseColor,
  ) as string

  const borderColor = variant === "outlined" ? baseColor : undefined
  const color = variant === "outlined" ? baseColor : "white"
  const backgroundColor = variant === "filled" ? baseColor : undefined
  const hoverBackgroundColor =
    variant === "filled"
      ? darken(finalHoverBackgroundColor, 0.1)
      : lighten(finalHoverBackgroundColor, 0.9)

  const chipProps: ChipProps = {
    onClick,
    sx: {
      backgroundColor,
      borderColor,
      color,
      "&.MuiChip-clickable:hover, &.MuiChip-clickable:active": {
        backgroundColor: `${hoverBackgroundColor} !important`,
      },
      ...sx,
    },
  }

  return (
    <Stack alignItems="center" gap={1} width="100%">
      <InfoDisplayLabel color={labelColor}>{label}</InfoDisplayLabel>
      <Chip
        label={count}
        variant="outlined"
        {...chipProps}
        sx={{ minWidth: "66px", width: "100%", ...(chipProps?.sx ?? {}) }}
      />
    </Stack>
  )
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography["10px"],
  textTransform: "uppercase",
  fontWeight: 600,
  color: theme.palette.primary.dark1,
}))

const InfoDisplayLabel = styled(Typography)<{ color?: string }>(
  ({ color, theme: { typography, palette } }) => ({
    ...typography["10px"],
    color: color ?? palette.primary.light1,
  }),
)
